/**
 * SortManager — implements tri-state column sorting for Argus Table.
 *
 * Sort cycle: first click → ascending, second → descending, third → original
 * order (null). Clicking a different column resets to ascending on the new
 * column.
 *
 * Supports custom sorter functions from column definitions, resolved either
 * by name from `window` or as a direct function reference. Falls back to
 * locale-aware string comparison when no custom sorter is available.
 */

import { resolveField } from './utils'

export class SortManager {
  constructor() {
    this._field = null
    this._direction = null
  }

  /**
   * Current sort state.
   *
   * @returns {{ field: string|null, direction: 'asc'|'desc'|null }}
   */
  get currentSort() {
    return { field: this._field, direction: this._direction }
  }

  /**
   * Advance the sort state for a column click.
   *
   * If the same column is clicked, cycle asc → desc → null.
   * If a different column is clicked, reset to asc on the new column.
   *
   * @param {string} field - Column field name
   * @returns {{ field: string|null, direction: 'asc'|'desc'|null }}
   */
  nextSortState(field) {
    if (this._field === field) {
      if (this._direction === 'asc') {
        this._direction = 'desc'
      } else if (this._direction === 'desc') {
        this._field = null
        this._direction = null
      } else {
        this._direction = 'asc'
      }
    } else {
      this._field = field
      this._direction = 'asc'
    }

    return this.currentSort
  }

  /**
   * Sort rows according to the current sort state.
   *
   * Returns a new array — the original is never mutated. When sort state
   * is null (no active sort), returns a shallow copy in original order.
   *
   * @param {Object[]} rows    - Rows to sort
   * @param {Object[]} columns - Column definitions (for sorter functions)
   * @returns {Object[]} Sorted rows (new array)
   */
  sort(rows, columns) {
    if (!this._field || !this._direction) {
      return [...rows]
    }

    const column = columns.find((col) => {
      return col.field === this._field
    })

    const comparator = this._resolveComparator(column)
    const direction = this._direction === 'asc' ? 1 : -1

    return [...rows].sort((a, b) => {
      return direction * comparator(a, b, column)
    })
  }

  // ── Private helpers ──────────────────────────────────────────────

  /**
   * Resolve the comparison function for a column.
   *
   * Checks for a custom `sorter` on the column definition (either a direct
   * function reference or a string name to look up on `window`). Falls back
   * to the default locale-aware string comparison.
   *
   * @param {Object|undefined} column - Column definition
   * @returns {Function} A comparator of the form (rowA, rowB, column) => number
   * @private
   */
  _resolveComparator(column) {
    if (!column) {
      return this._defaultComparator.bind(this)
    }

    const sorter = column.sorter

    if (typeof sorter === 'function') {
      return this._wrapCustomSorter(sorter, column)
    }

    if (typeof sorter === 'string' && sorter !== '') {
      const windowFn = window[sorter]

      if (typeof windowFn === 'function') {
        return this._wrapCustomSorter(windowFn, column)
      }

      console.warn(
        `[ArgusTable SortManager] sorter "${sorter}" not found on window, falling back to default comparison`
      )
    }

    return this._defaultComparator.bind(this)
  }

  /**
   * Wrap a custom sorter so that exceptions fall back to default comparison.
   *
   * Custom sorter signature: (a, b, rowA, rowB) => number
   * where a/b are the resolved field values and rowA/rowB are the full rows.
   *
   * @param {Function} sorterFn - The custom comparator
   * @param {Object}   column   - Column definition (for field resolution)
   * @returns {Function} A safe comparator of the form (rowA, rowB, column) => number
   * @private
   */
  _wrapCustomSorter(sorterFn, column) {
    const field = column.field

    return (rowA, rowB) => {
      const a = resolveField(rowA, field)
      const b = resolveField(rowB, field)

      try {
        return sorterFn(a, b, rowA, rowB)
      } catch (error) {
        console.warn(
          '[ArgusTable SortManager] custom sorter threw an error, falling back to default comparison',
          error
        )
        return this._defaultCompare(a, b)
      }
    }
  }

  /**
   * Default comparator that resolves field values and compares using
   * locale-aware string comparison.
   *
   * @param {Object} rowA   - First row
   * @param {Object} rowB   - Second row
   * @param {Object} column - Column definition
   * @returns {number}
   * @private
   */
  _defaultComparator(rowA, rowB, column) {
    const field = column ? column.field : this._field
    const a = resolveField(rowA, field)
    const b = resolveField(rowB, field)

    return this._defaultCompare(a, b)
  }

  /**
   * Locale-aware string comparison of two values.
   *
   * @param {*} a - First value
   * @param {*} b - Second value
   * @returns {number}
   * @private
   */
  _defaultCompare(a, b) {
    return String(a).localeCompare(String(b))
  }
}
